import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import classnames from 'classnames';

import { Typography } from '../../../components/Wrappers/Wrappers.jsx';
import DiscoverableToggleCard from './discoverableToggleCard';
import PhotoUpload from './PhotoUpload';
import useEcalWorldProfile from './useEcalWorldProfile';
import useCoverPhoto from './useCoverPhoto';
import useSaveFlash from './useSaveFlash';
import { useStyles } from './listingSection.styles';

function ListingSection ({ publisher, sectionRef }) {
    const {
        isPublic,
        confirmOpen,
        tooltipTitle,
        dialogTitle,
        dialogText,
        handleToggle,
        handleConfirm,
        handleCancel,
        renderErrorDialog: renderToggleErrorDialog,
    } = useEcalWorldProfile({ publisher });

    const {
        coverPhoto,
        handleUpload,
        handleDelete,
        renderErrorDialog: renderCoverPhotoErrorDialog,
    } = useCoverPhoto();

    const { saved, flashSaved } = useSaveFlash();
    const classes = useStyles({ isPublic });

    async function handleConfirmWithFlash () {
        await handleConfirm();
        flashSaved();
    }

    async function handleUploadWithFlash (dataUrl, file) {
        await handleUpload(dataUrl, file);
        flashSaved();
    }

    async function handleDeleteWithFlash () {
        await handleDelete();
        flashSaved();
    }

    return (
        <div className={classes.card} ref={sectionRef}>
            <div className={classes.headingRow}>
                <Typography variant={'h5'} weight={'bold'} className={classes.heading}>
                    Your ECAL World listing
                </Typography>
                <div className={classes.statusBadge}>
                    <span className={classes.statusDot} />
                    {isPublic ? 'Listed on ECAL World' : 'Not listed'}
                </div>
            </div>

            <Typography variant={'body1'} className={classes.description}>
                Control whether {publisher?.name || 'your organisation'} appears on ECAL World and how
                your tile looks to consumers.
            </Typography>

            <DiscoverableToggleCard
                isPublic={isPublic}
                confirmOpen={confirmOpen}
                tooltipTitle={tooltipTitle}
                dialogTitle={dialogTitle}
                dialogText={dialogText}
                onToggle={handleToggle}
                onConfirm={handleConfirmWithFlash}
                onCancel={handleCancel}
            />

            <PhotoUpload
                title={'Upload a Cover Image'}
                description={'This image will appear on your ECAL World profile.'}
                imageSource={coverPhoto}
                onUpload={handleUploadWithFlash}
                onDelete={handleDeleteWithFlash}
            />

            <Typography
                variant={'body2'}
                className={classnames(classes.saveFlash, { [classes.saveFlashVisible]: saved })}
            >
                ✓ Saved
            </Typography>

            { renderToggleErrorDialog() }
            { renderCoverPhotoErrorDialog() }
        </div>
    );
}

ListingSection.propTypes = {
    publisher: PropTypes.shape({
        name: PropTypes.string,
        publicAccess: PropTypes.bool,
    }),
    sectionRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
};

ListingSection.defaultProps = {
    publisher: {},
    sectionRef: null,
};

const ObservedListingSection = observer(ListingSection);
export default ObservedListingSection;
